import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Btn, Input, Select, Label, Empty, Badge } from "../components/UI";
import { SlidePanel } from "../components/SlidePanel";
import { RefillRequestsPanel } from "../components/rx/RefillRequestsPanel";
import { ClientSelect } from "./clinical/shared";
import { CocoaSelect } from "../components/CocoaSelect";
import { usePagination, PaginationBar } from "../components/Pagination";
import { CalendarClock, Pill, Check, X, Plus, Clock, Wrench, Package, FileText, Inbox, Users, Send, Play, CheckCircle2, Ban, Search } from "lucide-react";
import { toast } from "sonner";

dayjs.extend(relativeTime);

const TYPES = ["Time Off", "Schedule Change", "Client Transfer", "Supplies", "IT Support", "Records", "Other"];
const TYPE_ICON = { "Time Off": Clock, "Schedule Change": CalendarClock, "Client Transfer": Users, "Supplies": Package, "IT Support": Wrench, "Records": FileText, "Other": Inbox };
const STATUS_STYLE = {
  pending: "bg-[#FFF8E1] text-[#8D6E00]", in_progress: "bg-[#E3F2FD] text-[#1565C0]",
  approved: "bg-[#E8F5E9] text-[#2E7D32]", denied: "bg-[#FFEBEE] text-[#C62828]",
  completed: "bg-[#E0F2F1] text-[#00695C]", cancelled: "bg-[#EFEBE0] text-[#7B6157]",
};
const PRIORITY_DOT = { high: "#C62828", medium: "#EF6C00", low: "#2E7D32" };
const PRI_RANK = { high: 0, medium: 1, low: 2 };
const ST_RANK = { pending: 0, in_progress: 1, approved: 2, completed: 3, denied: 4, cancelled: 5 };
const EMPTY_REQ = { type: "Other", title: "", details: "", priority: "medium", assignee_id: "", client_id: "" };

const StatusChip = ({ s }) => (
  <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${STATUS_STYLE[s] || "bg-[#EFEBE0] text-[#6D4C41]"}`}>{String(s).replace("_", " ")}</span>
);

export default function Requests() {
  useEffect(() => { document.title = "Request Management | COCOA EHR"; }, []);
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin" || me?.is_super_admin;
  const openClient = (cid) => cid && navigate(`/clients/${cid}/console`);

  const [tab, setTab] = useState(isAdmin ? "all" : "assigned");
  const [counts, setCounts] = useState({});
  const [reqs, setReqs] = useState([]);
  const [appts, setAppts] = useState([]);
  const [refills, setRefills] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [statusF, setStatusF] = useState("");
  const [typeF, setTypeF] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_REQ);
  const [detail, setDetail] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const staffTab = ["all", "assigned", "mine"].includes(tab);

  const load = () => {
    api.get("/staff-requests/counts").then((r) => setCounts(r.data)).catch(() => {});
    api.get("/appointment-requests").then((r) => setAppts(r.data)).catch(() => {});
    api.get("/refill-requests").then((r) => setRefills(r.data)).catch(() => {});
  };
  const loadReqs = () => {
    if (!staffTab) return;
    api.get("/staff-requests", { params: { view: tab, status_f: statusF || undefined, type_f: typeF || undefined } })
      .then((r) => setReqs(r.data)).catch((e) => toast.error(apiError(e)));
  };
  useEffect(load, []);
  useEffect(loadReqs, [tab, statusF, typeF]);
  useEffect(() => {
    const rid = new URLSearchParams(window.location.search).get("req");
    if (rid) api.get(`/staff-requests/${rid}`).then((r) => { setComment(""); setDetail(r.data); }).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  const pendingAppts = appts.filter((r) => r.status === "pending");
  const pendingRefills = refills.filter((r) => r.status === "pending");
  const activeUsers = users.filter((u) => u.status === "active");

  const shown = useMemo(() => {
    const qq = search.trim().toLowerCase();
    const list = reqs.filter((r) => !qq || `${r.number} ${r.title} ${r.type} ${r.requester_name} ${r.assignee_name || ""} ${r.client_name || ""}`.toLowerCase().includes(qq));
    const by = {
      newest: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      oldest: (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
      priority: (a, b) => (PRI_RANK[a.priority] ?? 3) - (PRI_RANK[b.priority] ?? 3),
      status: (a, b) => (ST_RANK[a.status] ?? 9) - (ST_RANK[b.status] ?? 9),
    };
    return [...list].sort(by[sort] || by.newest);
  }, [reqs, search, sort]);
  const pg = usePagination(shown);

  const tabs = useMemo(() => [
    ...(isAdmin ? [["all", "All Requests", counts.all_pending]] : []),
    ["assigned", "Assigned to Me", counts.assigned_pending],
    ["mine", "My Requests", counts.mine_open],
    ["portal_appts", "Portal: Appointments", pendingAppts.length],
    ["portal_refills", "Refill Requests", pendingRefills.length],
  ], [isAdmin, counts, pendingAppts.length, pendingRefills.length]);

  const submitNew = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/staff-requests", form);
      toast.success("Request submitted");
      setNewOpen(false); setForm(EMPTY_REQ); load(); loadReqs();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const act = async (status) => {
    setBusy(true);
    try {
      const r = await api.put(`/staff-requests/${detail.id}`, { status });
      setDetail(r.data);
      toast.success(`Request ${status.replace("_", " ")}`);
      load(); loadReqs();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const sendComment = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try {
      await api.post(`/staff-requests/${detail.id}/comments`, { text: comment });
      const r = await api.get(`/staff-requests/${detail.id}`);
      setDetail(r.data); setComment("");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const reassign = async (assignee_id) => {
    try {
      const r = await api.put(`/staff-requests/${detail.id}`, { assignee_id });
      setDetail(r.data); toast.success("Assignee updated"); loadReqs(); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const canAct = detail && (isAdmin || detail.assignee_id === me?.id);
  const canCancel = detail && detail.requester_id === me?.id && ["pending", "in_progress"].includes(detail.status);

  const Actions = ({ onApprove, onDeny, id }) => (
    <div className="flex gap-1.5 ml-auto">
      <button data-testid={`request-approve-${id}`} onClick={onApprove} className="flex items-center gap-1 rounded-full bg-[#E8F5E9] px-3 py-1.5 text-xs font-bold text-[#2E7D32] hover:bg-[#C8E6C9]"><Check size={13} /> Approve</button>
      <button data-testid={`request-deny-${id}`} onClick={onDeny} className="flex items-center gap-1 rounded-full bg-[#FFEBEE] px-3 py-1.5 text-xs font-bold text-[#C62828] hover:bg-[#FFCDD2]"><X size={13} /> Deny</button>
    </div>
  );
  const decideAppt = async (r, status) => {
    try {
      await api.put(`/appointment-requests/${r.id}`, { status });
      toast.success(status === "approved" ? "Approved - appointment booked & client notified" : "Denied - client notified");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const decideRefill = async (r, action) => {
    try {
      await api.put(`/refill-requests/${r.id}`, { action });
      toast.success(`Refill ${action} - client notified`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="requests-page">
      <PageHeader title="Request Management" subtitle="Internal staff requests, and portal requests from clients, in one place"
        action={<Btn data-testid="new-request-btn" onClick={() => { setForm(EMPTY_REQ); setNewOpen(true); }}><Plus size={16} /> New Request</Btn>} />

      <div className="mb-4 flex flex-wrap gap-1.5" data-testid="requests-tabs">
        {tabs.map(([key, label, badge]) => (
          <button key={key} data-testid={`requests-tab-${key}`} onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${tab === key ? "bg-[#4E342E] text-white" : "bg-white border border-[#EFEBE0] text-[#6D4C41] hover:bg-[#F5F2EB]"}`}>
            {label}
            {badge > 0 && <span className={`rounded-full px-1.5 py-px text-[10px] font-bold ${tab === key ? "bg-[#FFB300] text-[#3E2723]" : "bg-[#FFB300] text-[#3E2723]"}`}>{badge}</span>}
          </button>
        ))}
      </div>

      {staffTab && (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#84675B]" />
              <Input data-testid="requests-search" className="h-10 w-56 pl-9" placeholder="Search requests…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <CocoaSelect testId="requests-status-filter" className="w-40" placeholder="All statuses" value={statusF} onChange={setStatusF}
              options={[["pending", "Pending"], ["in_progress", "In progress"], ["approved", "Approved"], ["denied", "Denied"], ["completed", "Completed"], ["cancelled", "Cancelled"]]} />
            <CocoaSelect testId="requests-type-filter" className="w-44" placeholder="All types" value={typeF} onChange={setTypeF} options={TYPES} />
            <CocoaSelect testId="requests-sort" className="w-44" placeholder="Newest first" value={sort === "newest" ? "" : sort} onChange={(v) => setSort(v || "newest")}
              options={[["oldest", "Oldest first"], ["priority", "By priority"], ["status", "By status"]]} />
          </div>
          <Card className="p-4" data-testid="staff-requests-panel">
            {shown.length === 0 ? <Empty text={reqs.length > 0 ? "No requests match your search" : tab === "mine" ? "You have not submitted any requests" : tab === "assigned" ? "No requests assigned to you" : "No requests found"} /> : (
              <div className="space-y-2">
                {pg.pageRows.map((r) => {
                  const I = TYPE_ICON[r.type] || Inbox;
                  return (
                    <button key={r.id} data-testid={`staff-request-${r.id}`} onClick={() => { setComment(""); setDetail(r); }}
                      className="flex w-full flex-wrap items-center gap-3 rounded-xl border border-[#F1EEE7] px-4 py-3 text-left transition-colors hover:bg-[#FDFCF8]">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F5F2EB] text-[#7B6157]"><I size={14} /></span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-sm font-bold text-[#3E2723]">
                          <span className="font-mono text-[10px] text-[#84675B]">{r.number}</span>
                          <span className="truncate">{r.title}</span>
                          <span className="h-2 w-2 shrink-0 rounded-full" title={`${r.priority} priority`} style={{ background: PRIORITY_DOT[r.priority] || "#8D6E63" }} />
                        </div>
                        <div className="text-xs text-[#7B6157]">{r.type} · {r.requester_name} → {r.assignee_name || "Unassigned"}{r.client_name ? ` · re: ${r.client_name}` : ""}</div>
                      </div>
                      <div className="ml-auto flex items-center gap-3">
                        <span className="text-[11px] text-[#84675B]">{dayjs(r.created_at).fromNow()}</span>
                        <StatusChip s={r.status} />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            <PaginationBar {...pg} testId="requests-pagination" />
          </Card>
        </>
      )}

      {tab === "portal_appts" && (
        <Card className="p-5" data-testid="appt-requests-panel">
          <h3 className="flex items-center gap-2 font-heading text-sm font-bold text-[#3E2723] mb-3">
            <CalendarClock size={15} className="text-[#7B6157]" /> Appointment Requests
            {pendingAppts.length > 0 && <span className="rounded-full bg-[#FFB300] px-2 py-0.5 text-[10px] font-bold text-[#3E2723]">{pendingAppts.length} pending</span>}
          </h3>
          {appts.length === 0 ? <Empty text="No appointment requests" /> : (
            <div className="space-y-2">
              {appts.slice(0, 25).map((r) => (
                <div key={r.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-[#F1EEE7] px-4 py-3" data-testid={`appt-request-${r.id}`}>
                  <div>
                    <div className="text-sm font-bold text-[#3E2723]"><button type="button" data-testid={`appt-request-client-${r.id}`} onClick={() => openClient(r.client_id)} className="hover:underline">{r.client_name || r.name}</button>{r.kind === "reschedule" && <span className="ml-1.5 rounded-full bg-[#EDE7F6] px-2 py-0.5 text-[10px] font-bold text-[#5E35B1]">RESCHEDULE</span>}</div>
                    <div className="text-xs text-[#7B6157]">{r.type || r.reason || "Appointment"} · prefers {r.preferred_date ? dayjs(r.preferred_date).format("MMM D") : "-"} {r.preferred_time || ""}</div>
                    {r.reason && <div className="text-[11px] text-[#84675B]">"{r.reason}"</div>}
                  </div>
                  {r.status === "pending"
                    ? <Actions id={r.id} onApprove={() => decideAppt(r, "approved")} onDeny={() => decideAppt(r, "denied")} />
                    : <span className="ml-auto"><Badge value={r.status} /></span>}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === "portal_refills" && (
        <Card className="p-5"><RefillRequestsPanel refills={refills} onChanged={load} openClient={openClient} /></Card>
      )}

      <SlidePanel open={newOpen} onClose={() => setNewOpen(false)} title="New Request" subtitle="Submit an internal request to a teammate or administrator" testId="new-request-panel">
        <form onSubmit={submitNew} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label>
              <Select data-testid="req-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </Select></div>
            <div><Label>Priority</Label>
              <Select data-testid="req-priority-select" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                {[["low", "Low"], ["medium", "Medium"], ["high", "High"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </Select></div>
          </div>
          <div><Label>Title</Label><Input data-testid="req-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Short summary" required maxLength={120} /></div>
          <div><Label>Assign to</Label>
            <Select data-testid="req-assignee-select" value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })}>
              <option value="">Unassigned (admin queue)</option>
              {activeUsers.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({String(u.role).replace("_", " ")})</option>)}
            </Select></div>
          <div><Label>Related client (optional)</Label>
            <ClientSelect testId="req-client-select" clients={clients} value={form.client_id}
              onChange={(id) => setForm({ ...form, client_id: id })} /></div>
          <div><Label>Details</Label>
            <textarea data-testid="req-details-input" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} rows={4}
              className="w-full rounded-xl border border-[#E5DFD3] bg-white px-3 py-2 text-sm text-[#3E2723] focus:outline-none focus:ring-2 focus:ring-[#8D6E63]" placeholder="Describe what you need…" /></div>
          <div className="flex justify-end gap-2">
            <Btn type="button" variant="ghost" onClick={() => setNewOpen(false)}>Cancel</Btn>
            <Btn data-testid="req-submit-btn" type="submit" disabled={busy}>{busy ? "Submitting…" : "Submit Request"}</Btn>
          </div>
        </form>
      </SlidePanel>

      <SlidePanel open={!!detail} onClose={() => setDetail(null)} title={detail ? `${detail.number} · ${detail.title}` : ""} subtitle={detail ? `${detail.type} · submitted ${dayjs(detail.created_at).format("MMM D, YYYY h:mm A")}` : ""} testId="request-detail-drawer">
        {detail && (
          <div className="space-y-4">
            <div className="rounded-xl bg-[#F5F2EB] p-4 text-sm text-[#3E2723]" data-testid="request-detail-info">
              <div className="flex justify-between py-0.5"><span className="text-[#7B6157]">Status</span><StatusChip s={detail.status} /></div>
              <div className="flex justify-between py-0.5"><span className="text-[#7B6157]">Priority</span>
                <span className="flex items-center gap-1.5 font-semibold capitalize"><span className="h-2 w-2 rounded-full" style={{ background: PRIORITY_DOT[detail.priority] }} />{detail.priority}</span></div>
              <div className="flex justify-between py-0.5"><span className="text-[#7B6157]">Requested by</span><span className="font-semibold">{detail.requester_name}</span></div>
              <div className="flex justify-between py-0.5"><span className="text-[#7B6157]">Assigned to</span><span className="font-semibold">{detail.assignee_name || "Unassigned"}</span></div>
              {detail.client_name && <div className="flex justify-between py-0.5"><span className="text-[#7B6157]">Client</span>
                <button className="font-semibold hover:underline" onClick={() => openClient(detail.client_id)}>{detail.client_name}</button></div>}
              {detail.decided_by && <div className="flex justify-between py-0.5"><span className="text-[#7B6157]">Decided by</span><span className="font-semibold">{detail.decided_by} · {dayjs(detail.decided_at).format("MMM D, h:mm A")}</span></div>}
            </div>
            {detail.details && <div className="rounded-xl border border-[#EFEBE0] p-4 text-sm text-[#5D4037] whitespace-pre-wrap" data-testid="request-detail-body">{detail.details}</div>}

            {(canAct || canCancel) && ["pending", "in_progress"].includes(detail.status) && (
              <div className="flex flex-wrap gap-2" data-testid="request-actions">
                {canAct && detail.status === "pending" && (
                  <Btn variant="outline" data-testid="request-start-btn" className="text-xs" disabled={busy} onClick={() => act("in_progress")}><Play size={13} /> Start Progress</Btn>
                )}
                {canAct && (
                  <>
                    <Btn data-testid="request-detail-approve-btn" className="text-xs bg-[#2E7D32] hover:bg-[#1B5E20]" disabled={busy} onClick={() => act("approved")}><Check size={13} /> Approve</Btn>
                    <Btn data-testid="request-detail-complete-btn" className="text-xs" disabled={busy} onClick={() => act("completed")}><CheckCircle2 size={13} /> Complete</Btn>
                    <Btn variant="ghost" data-testid="request-detail-deny-btn" className="text-xs text-[#C62828]" disabled={busy} onClick={() => act("denied")}><X size={13} /> Deny</Btn>
                  </>
                )}
                {canCancel && (
                  <Btn variant="ghost" data-testid="request-cancel-btn" className="text-xs text-[#7B6157]" disabled={busy} onClick={() => act("cancelled")}><Ban size={13} /> Cancel My Request</Btn>
                )}
              </div>
            )}

            {isAdmin && (
              <div><Label>Reassign to</Label>
                <Select data-testid="request-reassign-select" value={detail.assignee_id || ""} onChange={(e) => reassign(e.target.value)}>
                  <option value="">Unassigned</option>
                  {activeUsers.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
                </Select></div>
            )}

            <div>
              <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-[#7B6157]">Comments ({(detail.comments || []).length})</h4>
              <div className="max-h-56 space-y-2 overflow-y-auto pr-1" data-testid="request-comments">
                {(detail.comments || []).length === 0 && <div className="text-xs text-[#84675B]">No comments yet</div>}
                {(detail.comments || []).map((c) => (
                  <div key={c.id} className="rounded-xl border border-[#F1EEE7] px-3 py-2 text-sm">
                    <div className="flex items-center gap-2 text-[11px] text-[#84675B]"><b className="text-[#5D4037]">{c.by}</b>{dayjs(c.at).format("MMM D, h:mm A")}</div>
                    <div className="text-[#3E2723]">{c.text}</div>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <Input data-testid="request-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add a comment…"
                  onKeyDown={(e) => e.key === "Enter" && sendComment()} />
                <Btn data-testid="request-comment-send" disabled={busy || !comment.trim()} onClick={sendComment}><Send size={14} /></Btn>
              </div>
            </div>
          </div>
        )}
      </SlidePanel>
    </div>
  );
}
